import { slices } from "@/constants/sliceNames";
import { errorMessage, successMessage } from "@/store/actions/messages.actions";
import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../../axios/defaultAxios";
import { Permission, Role } from "../../../entities";
import { serializeQueryParams } from "../../../utils/utils";

const endpoint = "/policies";

/******* List all the roles *******/

export const requestRolesList = createAsyncThunk(
  `${slices.roles}/requestRolesList`,
  async (filter, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(`${endpoint}?${serializeQueryParams(filter)}`);
      return response.data.data.roles
        .map((role) => Role(role))
        .sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0));
    } catch (error) {
      dispatch(errorMessage(error.message));
      return rejectWithValue(error.message);
    }
  }
);

export const requestRolesListStatus = {
  [requestRolesList.fulfilled]: (state, action) => {
    state.loading = false;
    state.items = action.payload;
  },
  [requestRolesList.pending]: (state, action) => {
    state.loading = true;
  },
  [requestRolesList.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
  },
};

/******* Create a new role *******/

export const requestCreateRole = createAsyncThunk(
  `${slices.roles}/requestCreateRole`,
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(endpoint, data);
      dispatch(successMessage(response.data.message));
      return Role(data);
    } catch (error) {
      dispatch(errorMessage(error.response.data.error));
      return rejectWithValue(error.response.statusText);
    }
  }
);

export const requestCreateRoleStatus = {
  [requestCreateRole.fulfilled]: (state, action) => {
    state.loading = false;
    state.items.push(action.payload);
  },
  [requestCreateRole.pending]: (state, action) => {
    state.loading = true;
  },
  [requestCreateRole.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
  },
};

/****** Get Permissions *******/

export const requestPermissionsList = createAsyncThunk(
  `${slices.roles}/requestPermissionsList`,
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(`${endpoint}/permissions`);
      const permissions = response.data.data;
      return {
        permissions: Object.keys(permissions).flatMap((key) => Permission.createMany(key, permissions[key])),
        _permissions: permissions,
      };
    } catch (error) {
      dispatch(errorMessage(error.message));
      return rejectWithValue(error.message);
    }
  }
);

export const requestPermissionsListStatus = {
  [requestPermissionsList.fulfilled]: (state, action) => {
    state.permissionsLoading = false;
    state.permissions = action.payload.permissions;
    state._permissions = action.payload._permissions;
  },
  [requestPermissionsList.pending]: (state, action) => {
    state.permissionsLoading = true;
  },
  [requestPermissionsList.rejected]: (state, action) => {
    state.permissionsLoading = false;
    state.error = action.payload;
  },
};

/****** Delete Role *******/

export const requestDeleteRole = createAsyncThunk(
  `${slices.roles}/requestDeleteRole`,
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.delete(endpoint, { data });
      dispatch(successMessage(response.data.message));
      return data;
    } catch (error) {
      dispatch(errorMessage(error.response.data.error));
      return rejectWithValue(error.response.statusText);
    }
  }
);

export const requestDeleteRoleStatus = {
  [requestDeleteRole.fulfilled]: (state, action) => {
    state.items = state.items.filter(
      (role) => role.name !== action.payload.role || role.organization !== action.payload.organization
    );
    state.loading = false;
  },
  [requestDeleteRole.pending]: (state, action) => {
    state.loading = true;
  },
  [requestDeleteRole.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
  },
};
